import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  TextField,
  MenuItem,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import {
  Line,
  Bar,
  Pie,
  Doughnut,
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import './DataDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AVAILABLE_CHARTS = [
  { key: 'line',     label: 'Line', icon: ShowChartIcon },
  { key: 'bar',      label: 'Bar', icon: BarChartIcon },
  { key: 'pie',      label: 'Pie', icon: PieChartIcon },
  { key: 'doughnut', label: 'Doughnut', icon: DonutLargeIcon },
];

// Default color palette for pies/doughnuts
const DEFAULT_COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#66BB6A', '#FFA726',
  '#AB47BC', '#26A69A', '#EC407A', '#5C6BC0', '#D4E157'
];

const DEFAULT_LABEL_FORMATTER = (value) => value;

export default function DataDashboard({
  data,
  type,
  columns,
  dateFieldName = 'date',
  groupBy = 'month',      // 'month' or 'day'
  groupByField = null,    // if set, ignore dateFieldName & groupBy and use this field
  defaultChart = 'bar',
  labelFormatter = DEFAULT_LABEL_FORMATTER,
  labelLinkResolver = null,
  labelSort = 'alpha-asc',
  detailRows = null,
  detailFields = [],
  valueMetricOptions = null,
  defaultValueMetricKey = null,
}) {
  const [chartType, setChartType] = useState(defaultChart);
  const [summaryData, setSummaryData] = useState([]);
  const [chartData, setChartData] = useState({});
  const [chartKeys, setChartKeys] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const metricOptions = useMemo(() => {
    if (Array.isArray(valueMetricOptions) && valueMetricOptions.length) return valueMetricOptions;
    return [{ key: 'default', label: groupByField ? 'Count' : 'Amount', mode: groupByField ? 'count' : 'amount' }];
  }, [valueMetricOptions, groupByField]);

  const [activeMetricKey, setActiveMetricKey] = useState(
    defaultValueMetricKey || metricOptions[0]?.key || 'default'
  );

  useEffect(() => {
    if (!metricOptions.some((opt) => opt.key === activeMetricKey)) {
      setActiveMetricKey(defaultValueMetricKey || metricOptions[0]?.key || 'default');
    }
  }, [metricOptions, activeMetricKey, defaultValueMetricKey]);

  const selectedMetric = useMemo(() => {
    return metricOptions.find((opt) => opt.key === activeMetricKey) || metricOptions[0];
  }, [metricOptions, activeMetricKey]);

  const resolvedDetailRows = Array.isArray(detailRows) ? detailRows : data;

  const getGroupKey = (item) => {
    if (groupByField) return item[groupByField] ?? 'Unknown';
    const rawDate = item[dateFieldName];
    if (!rawDate) return 'Unknown';
    return groupBy === 'day'
      ? new Date(rawDate).toISOString().split('T')[0]
      : new Date(rawDate).toLocaleString('default', { month: 'long' });
  };

  const detailLookup = useMemo(() => {
    if (!Array.isArray(resolvedDetailRows)) return {};
    return resolvedDetailRows.reduce((acc, item) => {
      const key = getGroupKey(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [resolvedDetailRows, groupBy, groupByField, dateFieldName]);

  useEffect(() => {
    if (!Array.isArray(data)) return;

    // 1) Group items
    const readMetricValue = (item) => {
      if (selectedMetric?.mode === 'sum') {
        const raw = Number(item?.[selectedMetric.field]);
        return Number.isFinite(raw) ? raw : 0;
      }
      if (selectedMetric?.mode === 'amount') {
        const raw = Number(item?.amount ?? 1);
        return Number.isFinite(raw) ? raw : 0;
      }
      return 1;
    };

    const grouped = data.reduce((acc, item) => {
      const key = getGroupKey(item);
      acc[key] = (acc[key] || 0) + readMetricValue(item);
      return acc;
    }, {});

    // 2) Summary total
    const total = data.reduce((sum, i) => sum + readMetricValue(i), 0);
    setSummaryData([{
      label: metricOptions.length > 1 ? `Total ${type} (${selectedMetric?.label || 'Value'})` : `Total ${type}`,
      value: selectedMetric?.mode === 'sum' ? total.toFixed(2) : total,
    }]);

    // 3) Build chart payload
    let labels = Object.keys(grouped);
    if (labelSort === 'value-asc') {
      labels = labels.sort((a, b) => (grouped[a] || 0) - (grouped[b] || 0));
    } else if (labelSort === 'value-desc') {
      labels = labels.sort((a, b) => (grouped[b] || 0) - (grouped[a] || 0));
    } else {
      labels = labels.sort();
    }
    const displayLabels = labels.map((label) => labelFormatter(label ?? 'Unknown'));
    const values = labels.map(l => grouped[l]);
    setChartKeys(labels);

    // Determine background colors
    const backgroundColors =
      groupByField || chartType === 'pie' || chartType === 'doughnut'
        ? labels.map((_, idx) => DEFAULT_COLORS[idx % DEFAULT_COLORS.length])
        : '#c2171f';

    // Border colors can mirror background for pies
    const borderColors =
      Array.isArray(backgroundColors)
        ? backgroundColors
        : '#c2171f';

    setChartData({
      labels: displayLabels,
      datasets: [{
        label: `${selectedMetric?.label || type} by ${groupByField || groupBy}`,
        data: values,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        fill: (!groupByField && groupBy === 'day') || false,
        tension: 0.4,
      }]
    });
  }, [data, type, dateFieldName, groupBy, groupByField, chartType, labelFormatter, selectedMetric, metricOptions.length]);

  const handlePointClick = (event, elements, chart) => {
    let index = null;

    if (elements && elements.length > 0) {
      index = elements[0].index;
    } else if (labelLinkResolver && chart?.scales?.x) {
      const xScale = chart.scales.x;
      const pixel = event?.x;
      if (typeof pixel === 'number') {
        const raw = xScale.getValueForPixel(pixel);
        const rounded = Math.round(raw);
        if (Number.isInteger(rounded) && rounded >= 0 && rounded < chartKeys.length) {
          const key = chartKeys[rounded];
          const link = labelLinkResolver(key);
          if (link) {
            window.open(link, '_blank', 'noopener,noreferrer');
            return;
          }
        }
      }
      return;
    } else {
      return;
    }

    if (index == null) return;
    const key = chartKeys[index];
    if (!key) return;
    const label = labelFormatter(key ?? 'Unknown');
    const rows = detailLookup[key] || [];
    setSelectedDetail({ key, label, rows });
  };

  const SummaryMetricIcon = useMemo(() => {
    if (/promo/i.test(type)) return StorefrontIcon;
    if (/lead/i.test(type)) return LightbulbOutlinedIcon;
    if (/project|order/i.test(type)) return AssignmentTurnedInOutlinedIcon;
    return InsightsOutlinedIcon;
  }, [type]);

  const renderChart = () => {
    if (!chartData.labels) return <p>No data available</p>;

    const opts = {
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: handlePointClick,
        plugins: {
          title: { display: true, text: chartData.datasets[0].label },
          legend: { display: true }
        },
        ...(groupByField || chartType === 'pie' || chartType === 'doughnut'
          ? {}
          : {
              scales: {
                x: { title: { display: true, text: groupBy } },
                y: { title: { display: true, text: selectedMetric?.label || `# of ${type}` }, beginAtZero: true }
              }
            }
        )
      }
    };

    switch (chartType) {
      case 'line':   return <Line {...opts} />;
      case 'bar':    return <Bar {...opts} />;
      case 'pie':    return <Pie {...opts} />;
      case 'doughnut': return <Doughnut {...opts} />;
      default:       return <p>Unsupported chart type.</p>;
    }
  };

  return (
    <>
      <div className="chart-dashboard">
        <div className="chart-controls" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Chart style:</Typography>
            {AVAILABLE_CHARTS.map((chart) => {
              const Icon = chart.icon;
              const active = chartType === chart.key;
              return (
                <MuiTooltip key={chart.key} title={chart.label}>
                  <IconButton
                    onClick={() => setChartType(chart.key)}
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: active ? 'primary.main' : 'divider',
                      color: active ? 'primary.main' : 'text.secondary',
                      backgroundColor: active ? 'rgba(25,118,210,0.08)' : 'transparent',
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                </MuiTooltip>
              );
            })}
          </Box>
          {metricOptions.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Filter by:</Typography>
              <TextField
                select
                size="small"
                value={activeMetricKey}
                onChange={(event) => setActiveMetricKey(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </div>
        <div className="charts">
          <div className="chart-container">{renderChart()}</div>
        </div>
      </div>

      <div className="data-dashboard">
        <div className="dashboard-header">
          <AutoGraphIcon fontSize="small" className="dashboard-header-icon" />
          <h2>{type} Dashboard</h2>
        </div>

        <div className="summary-cards">
          {summaryData.map((r, i) => (
            <div key={i} className="summary-card">
              <div className="summary-card-top">
                <span className="summary-card-icon" aria-hidden><SummaryMetricIcon fontSize="small" /></span>
                <span className="summary-card-label">{r.label}</span>
              </div>
              <div className="summary-card-value">{r.value}</div>
            </div>
          ))}
        </div>

        {/*<div className="data-table">
          <h3>{type} Data</h3>
          <DataGrid
            rows={data.map((r, i) => ({ id: i + 1, ...r }))}
            columns={columns}
            pageSize={5}
            autoHeight
          />
              </div>*/}
      </div>

      <Dialog
        open={Boolean(selectedDetail)}
        onClose={() => setSelectedDetail(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedDetail?.label || 'Details'}</DialogTitle>
        <DialogContent dividers>
          {selectedDetail?.rows?.length ? (
            <List dense>
              {selectedDetail.rows.map((row, idx) => (
                <ListItem key={`${selectedDetail.key}-${idx}`} alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 700 }}>
                        {row.promoStoreName ||
                          row.customerName ||
                          row.wooOrderNumber ||
                          row.id ||
                          `Row ${idx + 1}`}
                      </Typography>
                    }
                    secondary={
                      detailFields.length ? (
                        detailFields.map((field) => {
                          const rawValue = row[field.key];
                          const value = field.format ? field.format(rawValue, row) : rawValue;
                          return (
                            <div key={field.key}>
                              <strong>{field.label}:</strong> {value ?? '—'}
                            </div>
                          );
                        })
                      ) : (
                        <div>{JSON.stringify(row)}</div>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
